import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BizwebEntityType, type ContributorCategoryMapToken, type ReviewToken, type AwardToken } from '@sleed/bizcommerce-api';
import { contributorsApi } from '~/lib/api/client';
import { reviewKeys } from '~/hooks/use-reviews';

/**
 * Per-contributor row sub-views (parity with the legacy ContributorsCategoryMap / ContributorsReviews /
 * ContributorAwards modals). Each is keyed by the parent contributor id; mutations stamp the parent
 * linkage the legacy `insert(...)` helpers added before posting.
 */

const subviewKeys = {
	categoryMap: (id: number) => ['contributor-subview', 'category-map', id] as const,
	reviews: (id: number) => ['contributor-subview', 'reviews', id] as const,
	awards: (id: number) => ['contributor-subview', 'awards', id] as const
};

/* ── Category map (Κατηγορίες) ─────────────────────────────────────────────── */

export function useContributorCategoryMap(contributorId: number | null) {
	return useQuery<ContributorCategoryMapToken[]>({
		queryKey: subviewKeys.categoryMap(contributorId ?? 0),
		queryFn: async () => (await contributorsApi.getAllCategoriesById(contributorId!)).data.items ?? [],
		enabled: contributorId != null && contributorId > 0
	});
}

export function useContributorCategoryMapMutations(contributorId: number) {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: subviewKeys.categoryMap(contributorId) });
	const create = useMutation({
		mutationFn: async (token: ContributorCategoryMapToken) =>
			(await contributorsApi.insertContributorCategory({ ...token, conId: contributorId })).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (token: ContributorCategoryMapToken) => (await contributorsApi.updateContributorCategory(token)).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async (id: number) => (await contributorsApi.deleteContributorCategory(id)).data,
		onSuccess: invalidate
	});
	return { create, update, remove };
}

/* ── Reviews (Κριτικές) ─────────────────────────────────────────────────────── */

export function useContributorReviews(contributorId: number | null) {
	return useQuery<ReviewToken[]>({
		queryKey: subviewKeys.reviews(contributorId ?? 0),
		queryFn: async () => (await contributorsApi.getReviewsById(contributorId!)).data.items ?? [],
		enabled: contributorId != null && contributorId > 0
	});
}

/**
 * Editorial reviews of one contributor. The server validates them (an existing contributor and target, never the
 * "revisioned" status) and recomputes the ratings; the Reviews page grids and its badge refresh with the sub-grid.
 */
export function useContributorReviewMutations(contributorId: number) {
	const qc = useQueryClient();
	const invalidate = () =>
		Promise.all([
			qc.invalidateQueries({ queryKey: subviewKeys.reviews(contributorId) }),
			qc.invalidateQueries({ queryKey: reviewKeys.all })
		]);
	const create = useMutation({
		mutationFn: async (token: ReviewToken) =>
			(
				await contributorsApi.insertContributorReview({
					...token,
					from_id: contributorId,
					from_ent: BizwebEntityType.Contributor,
					to_ent: BizwebEntityType.Product
				})
			).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (token: ReviewToken) => (await contributorsApi.updateContributorReview(token)).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async (id: number) => (await contributorsApi.deleteReview(id)).data,
		onSuccess: invalidate
	});
	return { create, update, remove };
}

/* ── Awards (Βραβεία) ───────────────────────────────────────────────────────── */

export function useContributorAwards(contributorId: number | null) {
	return useQuery<AwardToken[]>({
		queryKey: subviewKeys.awards(contributorId ?? 0),
		queryFn: async () => (await contributorsApi.getAllContributorAwardsById(contributorId!)).data.items ?? [],
		enabled: contributorId != null && contributorId > 0
	});
}

export function useContributorAwardMutations(contributorId: number) {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: subviewKeys.awards(contributorId) });
	const create = useMutation({
		mutationFn: async (token: AwardToken) =>
			(await contributorsApi.insertContributorAward({ ...token, to_id: contributorId, to_ent: BizwebEntityType.Contributor })).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (token: AwardToken) => (await contributorsApi.updateContributorAward(token)).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async (id: number) => (await contributorsApi.deleteContributorAward(id)).data,
		onSuccess: invalidate
	});
	return { create, update, remove };
}
